// Config Template — export + import.
//
// Generates an Excel workbook with one sheet per (model, length) variant group,
// pre-populated with the spec's base + accessory fields. Every option cell has
// a dropdown (data-validation list) so the client can pick from valid options.
// Existing variants are written as columns; spare "New Variant" columns let
// the client define additional configurations.
//
// Importing reads the same workbook back, resolves option labels to spec
// values, and returns an array of variant configs ready to be merged into
// the wizard state. Inclusions/exclusions are NOT stored in the file — they
// are auto-derived downstream from the chosen field values.

import ExcelJS from "exceljs";
import { specForUnitModel, defaultsFor } from "../data/orderingCodes";

// ----------------------------------------------------------------------------
// Internals
// ----------------------------------------------------------------------------

const SPARE_VARIANT_SLOTS = 3; // blank columns the client can fill in

function newId() {
  return `cfg_${Math.random().toString(36).slice(2, 9)}_${Date.now()}`;
}

function groupKey(unitModel, lengthFt) {
  const spec = specForUnitModel(unitModel);
  return `${spec?.id || unitModel}__${lengthFt ?? ""}`;
}

// Escape commas + double-quotes in option labels so they don't break the
// data-validation formula. Excel uses "" for embedded quotes.
function escapeForList(label) {
  return String(label).replace(/"/g, '""').replace(/,/g, " ");
}

function buildListFormula(field) {
  const labels = field.options.map((o) => escapeForList(o.label));
  return [`"${labels.join(",")}"`];
}

function labelForOptionValue(field, val) {
  const match = field.options.find((o) => o.value === val);
  if (match) return match.label;
  // Fallback: stringify
  return val == null ? "" : String(val);
}

function valueForOptionLabel(field, label) {
  if (label == null || label === "") return undefined;
  const trimmed = String(label).trim();
  const direct = field.options.find(
    (o) => String(o.label).trim().toLowerCase() === trimmed.toLowerCase()
  );
  if (direct) return direct.value;
  // Tolerate raw values typed instead of labels (e.g. user typed "0").
  const asNum = Number(trimmed);
  if (Number.isFinite(asNum)) {
    const numMatch = field.options.find((o) => o.value === asNum);
    if (numMatch) return numMatch.value;
  }
  const strMatch = field.options.find((o) => String(o.value) === trimmed);
  if (strMatch) return strMatch.value;
  // Legacy fallback: if the saved value isn't in the current option set but
  // looks numeric (e.g. a pre-existing project stored lint_filter=2 when the
  // spec lists only 0/1), preserve it. The code builder will still encode it
  // and the round-trip stays lossless.
  if (Number.isFinite(asNum) && String(asNum) === trimmed) return asNum;
  return undefined;
}

// Apply our standard styling to a header cell.
function styleHeader(cell, opts = {}) {
  cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: opts.color || "FF0F172A" }, // slate-900
  };
  cell.alignment = { vertical: "middle", horizontal: opts.align || "left" };
  cell.border = {
    top: { style: "thin", color: { argb: "FFCBD5E1" } },
    bottom: { style: "thin", color: { argb: "FFCBD5E1" } },
    left: { style: "thin", color: { argb: "FFCBD5E1" } },
    right: { style: "thin", color: { argb: "FFCBD5E1" } },
  };
}

function styleLabelCell(cell, indent = false) {
  cell.font = { size: 10, color: { argb: "FF334155" } };
  if (indent) cell.alignment = { indent: 1, vertical: "middle" };
  cell.border = {
    top: { style: "hair", color: { argb: "FFE2E8F0" } },
    bottom: { style: "hair", color: { argb: "FFE2E8F0" } },
    left: { style: "hair", color: { argb: "FFE2E8F0" } },
    right: { style: "hair", color: { argb: "FFE2E8F0" } },
  };
}

function styleValueCell(cell) {
  cell.font = { size: 10 };
  cell.alignment = { vertical: "middle", horizontal: "left" };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF8FAFC" }, // slate-50
  };
  cell.border = {
    top: { style: "hair", color: { argb: "FFE2E8F0" } },
    bottom: { style: "hair", color: { argb: "FFE2E8F0" } },
    left: { style: "hair", color: { argb: "FFE2E8F0" } },
    right: { style: "hair", color: { argb: "FFE2E8F0" } },
  };
}

function styleSectionRow(cell) {
  cell.font = { bold: true, size: 10, color: { argb: "FF1E293B" } };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFE2E8F0" },
  };
  cell.alignment = { vertical: "middle", horizontal: "left" };
}

// ----------------------------------------------------------------------------
// EXPORT
// ----------------------------------------------------------------------------

/**
 * Build an Excel workbook with one worksheet per (model, length) group.
 * Each sheet contains the spec's editable fields as rows and one column per
 * variant (existing + spare blanks). Every value cell has a dropdown
 * validation listing valid options for that field.
 *
 * @param {object} project           Project record (name, number).
 * @param {Array}  taggedVariants    configurations already passed through withVariantTags.
 * @param {Array}  groups            from buildUnitSchedule(rooms, results).
 * @returns {Promise<Blob>} XLSX blob ready to download.
 */
export async function buildConfigTemplate(project, taggedVariants, groups) {
  const wb = new ExcelJS.Workbook();
  wb.creator = "ZAMBA Selection Tool";
  wb.created = new Date();

  // Group variants by (model, length) so each sheet renders only its own.
  const variantsByKey = new Map();
  (taggedVariants || []).forEach((v) => {
    if (!variantsByKey.has(v.key)) variantsByKey.set(v.key, []);
    variantsByKey.get(v.key).push(v);
  });

  // Cover sheet — quick legend + how-to.
  const cover = wb.addWorksheet("Read me", { views: [{ showGridLines: false }] });
  cover.getColumn("A").width = 110;
  const lines = [
    "ZAMBA Configuration Template",
    "",
    `Project: ${project?.name || "—"}${project?.number ? `  ·  No. ${project.number}` : ""}`,
    `Generated: ${new Date().toISOString().slice(0, 10)}`,
    "",
    "How to use:",
    "  1. Pick options from the dropdowns in each Variant column.",
    "  2. Add new variants by filling the empty 'New Variant ▢' columns.",
    "     Rename them in the 'Variant Name' row (row 7).",
    "  3. Save and email the workbook back to your project contact.",
    "  4. Inside the tool: Configurator tab → Import Config → pick the file.",
    "",
    "Notes:",
    "  • One worksheet per unit type + length combination.",
    "  • Only options shown in the dropdowns are accepted by the import.",
    "  • Tags ending with a letter (e.g. EWS-1A) are auto-assigned.",
    "  • Inclusions / Exclusions are derived automatically — no manual entry needed.",
  ];
  lines.forEach((t, i) => {
    const c = cover.getCell(`A${i + 1}`);
    c.value = t;
    if (i === 0) {
      c.font = { bold: true, size: 16, color: { argb: "FF0F172A" } };
    } else if (t.endsWith(":")) {
      c.font = { bold: true, size: 11, color: { argb: "FF0F172A" } };
    } else {
      c.font = { size: 10, color: { argb: "FF334155" } };
    }
  });

  (groups || []).forEach((g) => {
    const spec = specForUnitModel(g.unit_model);
    if (!spec) return;

    const key = groupKey(g.unit_model, g.length_ft);
    const existing = variantsByKey.get(key) || [];
    const totalCols = existing.length + SPARE_VARIANT_SLOTS;

    // Worksheet name — sanitized + length suffix; Excel limit 31 chars.
    const lenSuffix = g.length_ft != null ? `_${g.length_ft}ft` : "";
    const wsName = `${g.unit_model}${lenSuffix}`
      .replace(/[\\/?*[\]]/g, "")
      .slice(0, 31);
    const ws = wb.addWorksheet(wsName, { views: [{ state: "frozen", ySplit: 8 }] });

    // Column widths.
    ws.getColumn("A").width = 14;  // Section
    ws.getColumn("B").width = 36;  // Field label
    ws.getColumn("C").width = 22;  // Field key (parser hint)
    ws.getColumn("D").width = 26;  // Default / standard
    for (let i = 0; i < totalCols; i++) {
      ws.getColumn(5 + i).width = 24;
    }

    // Row 1: title.
    ws.mergeCells(1, 1, 1, Math.max(4, 4 + totalCols));
    const titleCell = ws.getCell("A1");
    titleCell.value = `${g.unit_model}${g.length_ft != null ? ` · ${g.length_ft} ft` : ""}`;
    titleCell.font = { bold: true, size: 14, color: { argb: "FF0F172A" } };
    titleCell.alignment = { vertical: "middle" };

    // Row 2: project info.
    ws.mergeCells(2, 1, 2, Math.max(4, 4 + totalCols));
    const subCell = ws.getCell("A2");
    subCell.value = `Project: ${project?.name || "—"}${project?.number ? `   ·   No. ${project.number}` : ""}`;
    subCell.font = { size: 10, color: { argb: "FF64748B" } };

    // Row 3: parser metadata (group key) — needed for import to know which
    // group this sheet maps to. Cell B3 holds the canonical key.
    ws.getCell("A3").value = "Group Key (do not edit)";
    ws.getCell("A3").font = { size: 9, color: { argb: "FF94A3B8" }, italic: true };
    ws.getCell("B3").value = key;
    ws.getCell("B3").font = { size: 9, color: { argb: "FF94A3B8" }, italic: true };

    // Row 5: header row.
    const HEADER_ROW = 5;
    ["Section", "Field", "Key", "Default"].forEach((h, i) => {
      const cell = ws.getCell(HEADER_ROW, 1 + i);
      cell.value = h;
      styleHeader(cell);
    });
    for (let i = 0; i < totalCols; i++) {
      const cell = ws.getCell(HEADER_ROW, 5 + i);
      const isExisting = i < existing.length;
      cell.value = isExisting
        ? `Variant ${i + 1}`
        : `New Variant ${i - existing.length + 1}`;
      styleHeader(cell, { color: isExisting ? "FF0F172A" : "FF065F46", align: "center" });
    }

    // Row 6: Tag row (auto-assigned, read-only hint).
    const TAG_ROW = 6;
    ws.getCell(TAG_ROW, 2).value = "Tag (auto-assigned)";
    styleLabelCell(ws.getCell(TAG_ROW, 2));
    ws.getCell(TAG_ROW, 3).value = "__tag";
    styleLabelCell(ws.getCell(TAG_ROW, 3));
    for (let i = 0; i < totalCols; i++) {
      const cell = ws.getCell(TAG_ROW, 5 + i);
      cell.value = i < existing.length ? existing[i].tag || "" : "";
      cell.font = { size: 10, italic: true, color: { argb: "FF64748B" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF1F5F9" } };
    }

    // Row 7: Variant name (editable).
    const NAME_ROW = 7;
    ws.getCell(NAME_ROW, 2).value = "Variant Name";
    styleLabelCell(ws.getCell(NAME_ROW, 2));
    ws.getCell(NAME_ROW, 3).value = "__name";
    styleLabelCell(ws.getCell(NAME_ROW, 3));
    for (let i = 0; i < totalCols; i++) {
      const cell = ws.getCell(NAME_ROW, 5 + i);
      cell.value = i < existing.length ? existing[i].name || "" : "";
      styleValueCell(cell);
    }

    // Row 8: blank spacer.
    let row = 8;

    // Helper to render a section block.
    const renderSection = (sectionLabel, fields, group) => {
      // Section header row.
      const sCell = ws.getCell(row, 1);
      sCell.value = sectionLabel;
      styleSectionRow(sCell);
      for (let c = 2; c <= 4 + totalCols; c++) {
        styleSectionRow(ws.getCell(row, c));
      }
      row++;

      fields.forEach((f) => {
        // Skip auto-populated fields (e.g. nozzle_type, num_nozzles) — they
        // are driven by the selection engine, not a customer choice.
        if (f.auto) return;
        ws.getCell(row, 1).value = sectionLabel;
        styleLabelCell(ws.getCell(row, 1), true);
        ws.getCell(row, 2).value = f.label;
        styleLabelCell(ws.getCell(row, 2));
        ws.getCell(row, 3).value = `${group}.${f.key}`;
        styleLabelCell(ws.getCell(row, 3));
        const defLabel = labelForOptionValue(f, f.default);
        ws.getCell(row, 4).value = defLabel;
        styleLabelCell(ws.getCell(row, 4));

        // Variant value columns
        const listFormula = buildListFormula(f);
        for (let i = 0; i < totalCols; i++) {
          const cell = ws.getCell(row, 5 + i);
          if (i < existing.length) {
            // Existing variant — pre-fill with its current value.
            cell.value = labelForOptionValue(f, existing[i][group]?.[f.key]);
          } else {
            // Spare "New Variant ▢" column — leave blank; the Default column
            // already shows the standard option.
            cell.value = "";
          }
          styleValueCell(cell);
          cell.dataValidation = {
            type: "list",
            allowBlank: true,
            formulae: listFormula,
            showErrorMessage: true,
            errorStyle: "warning",
            errorTitle: "Invalid option",
            error: `Pick one of the listed options for "${f.label}".`,
          };
        }

        // For RAL fields, append a "Custom RAL code" row so the client can
        // specify a 4-digit code when "Custom RAL" is picked.
        if (f.type === "ral") {
          row++;
          ws.getCell(row, 1).value = sectionLabel;
          styleLabelCell(ws.getCell(row, 1), true);
          ws.getCell(row, 2).value = `↳ ${f.label} – Custom RAL code (4 digits)`;
          styleLabelCell(ws.getCell(row, 2));
          ws.getCell(row, 3).value = `extras.${f.key}_ral`;
          styleLabelCell(ws.getCell(row, 3));
          ws.getCell(row, 4).value = "9075";
          styleLabelCell(ws.getCell(row, 4));
          for (let i = 0; i < totalCols; i++) {
            const cell = ws.getCell(row, 5 + i);
            const ralVal =
              i < existing.length
                ? existing[i].extras?.[`${f.key}_ral`] || ""
                : "";
            cell.value = ralVal;
            styleValueCell(cell);
          }
        }

        row++;
      });
    };

    renderSection("Base Unit", spec.baseFields, "base");
    row++; // spacer
    renderSection("Accessories", spec.accessoryFields, "accessory");
  });

  const buf = await wb.xlsx.writeBuffer();
  return new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

// ----------------------------------------------------------------------------
// IMPORT
// ----------------------------------------------------------------------------

/**
 * Parse a config-template workbook back into variant config objects.
 *
 * Returns { variants: [ ... ], warnings: [ ... ] }
 *   variants — array of new variant configs ready to be merged into
 *              the project's configurations[] list.
 *   warnings — human-readable strings (per-cell mismatches, blank sheets).
 *
 * Only sheets whose Group Key matches a known (model, length) group in the
 * current project's `groups` argument are processed. Unknown sheets are
 * surfaced as warnings.
 */
export async function parseConfigTemplate(arrayBuffer, groups) {
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(arrayBuffer);

  const validKeys = new Set(
    (groups || []).map((g) => groupKey(g.unit_model, g.length_ft))
  );
  // Index group by key so we can resolve the spec from the sheet.
  const groupsByKey = new Map();
  (groups || []).forEach((g) =>
    groupsByKey.set(groupKey(g.unit_model, g.length_ft), g)
  );

  const variants = [];
  const warnings = [];

  wb.eachSheet((ws) => {
    if (ws.name === "Read me") return;
    const keyCell = ws.getCell("B3").value;
    const sheetKey = keyCell ? String(keyCell).trim() : "";
    if (!sheetKey) {
      warnings.push(`Sheet "${ws.name}" missing Group Key — skipped.`);
      return;
    }
    if (!validKeys.has(sheetKey)) {
      warnings.push(
        `Sheet "${ws.name}" (key ${sheetKey}) does not match any unit in the project — skipped.`
      );
      return;
    }
    const group = groupsByKey.get(sheetKey);
    const spec = specForUnitModel(group.unit_model);
    if (!spec) {
      warnings.push(`No ordering spec for ${group.unit_model} — skipped.`);
      return;
    }

    // Discover variant columns: every column from E onward whose header row
    // (row 5) is non-empty.
    const HEADER_ROW = 5;
    const TAG_ROW = 6;
    const NAME_ROW = 7;
    const variantCols = [];
    for (let c = 5; c <= ws.columnCount; c++) {
      const v = ws.getCell(HEADER_ROW, c).value;
      if (v != null && String(v).trim() !== "") variantCols.push(c);
    }

    // Build a lookup from key (e.g. "base.length") to field spec + group.
    const fieldByKey = new Map();
    spec.baseFields.forEach((f) =>
      fieldByKey.set(`base.${f.key}`, { group: "base", field: f })
    );
    spec.accessoryFields.forEach((f) =>
      fieldByKey.set(`accessory.${f.key}`, { group: "accessory", field: f })
    );

    // Scan rows 8..end. Each row's column C holds the field key.
    variantCols.forEach((c) => {
      // Initialise variant with spec defaults.
      const defaults = defaultsFor(spec);
      const variant = {
        id: newId(),
        key: sheetKey,
        model_id: spec.id,
        name: "",
        assignments: [],
        base: { ...defaults.base },
        accessory: { ...defaults.accessory },
        extras: {
          enclosure_color_ral: "9075",
          grille_color_ral: "9075",
          color_ral: "9075",
        },
      };

      // Lock the length field to the sheet's length_ft (we don't accept
      // mixed lengths inside a single (model, length) group).
      if (group.length_ft != null) {
        const lenField = spec.baseFields.find((f) => f.key === "length");
        if (lenField && lenField.options.some((o) => o.value === group.length_ft)) {
          variant.base.length = group.length_ft;
        }
      }

      // Name + tag
      const nameVal = ws.getCell(NAME_ROW, c).value;
      variant.name = nameVal ? String(nameVal).trim() : `Imported ${c - 4}`;
      // Tag is auto-assigned downstream; ignore TAG_ROW values.
      void TAG_ROW;

      let touched = false;

      for (let r = 8; r <= ws.rowCount; r++) {
        const keyCellVal = ws.getCell(r, 3).value;
        if (!keyCellVal) continue;
        const k = String(keyCellVal).trim();
        if (!k || k.startsWith("__")) continue;

        const valCellVal = ws.getCell(r, c).value;
        if (valCellVal == null || valCellVal === "") continue;
        touched = true;

        // RAL custom code rows go to extras
        if (k.startsWith("extras.")) {
          const extraKey = k.slice("extras.".length);
          const ralStr = String(valCellVal).replace(/[^0-9]/g, "").slice(0, 4);
          if (ralStr) variant.extras[extraKey] = ralStr;
          continue;
        }

        const entry = fieldByKey.get(k);
        if (!entry) {
          warnings.push(
            `Sheet "${ws.name}" row ${r}: unknown field key "${k}" — ignored.`
          );
          continue;
        }
        const { group: g, field: f } = entry;
        const resolved = valueForOptionLabel(f, valCellVal);
        if (resolved === undefined) {
          warnings.push(
            `Sheet "${ws.name}" row ${r} (${f.label}): unrecognised value "${valCellVal}" — kept default.`
          );
          continue;
        }
        variant[g][f.key] = resolved;
      }

      if (touched) variants.push(variant);
    });
  });

  return { variants, warnings };
}

// ----------------------------------------------------------------------------
// Browser download helper
// ----------------------------------------------------------------------------
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 0);
}
